import Correspondence from '@/models/Correspondence';
import VariationOrder from '@/models/VariationOrder';
import VariationOrderService from '@/services/VariationOrderService';
import { defineStore } from 'pinia';
import { useRepo } from 'pinia-orm';
import { useCommentsStore } from './comments';
import { useCorrespondencesStore } from './correspondences';
import { useTradeItemsStore } from './tradeItems';

export const useVariationOrdersStore = defineStore('variation_orders', {
  state: () => ({}),

  getters: {
    models: () => useRepo(VariationOrder),
  },

  actions: {
    async fetchVariationOrders(projectId: Id, form: ApiRequests['api.v1.variation-orders.index'] = {}) {
      const [data, error] = await VariationOrderService.index(projectId, form);

      if(error) {
        throw error;
      }

      this.models.save(data.data);

      return data;
    },

    async fetchApprovedVariationOrders(projectId: Id) {
      const [data, error] = await VariationOrderService.claimIndex(projectId);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async fetchVariationOrder(id: Id) {
      const [data, error] = await VariationOrderService.show(id);

      if(error) {
        throw error;
      }

      this.models.save(data.data);

      return data;
    },

    async acceptVariationOrder(id: Id) {
      const [data, error] = await VariationOrderService.accept(id);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async principleVariationOrder(id: Id) {
      const [data, error] = await VariationOrderService.principle(id);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async declineVariationOrder(id: Id) {
      const [data, error] = await VariationOrderService.decline(id);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async priceVariationOrder(id: Id, form: ApiRequests['api.v1.variation-orders.pricing']) {
      const [data, error] = await VariationOrderService.price(id, form);

      if(error) {
        throw error;
      }

      const tradeItemsStore = useTradeItemsStore();

      tradeItemsStore.models.where('variation_order_id', parseInt(id as string)).delete();

      this.models.save(data);

      return data;
    },

    async repriceVariationOrder(id: Id, form: ApiRequests['api.v1.variation-orders.reprice']) {
      const [data, error] = await VariationOrderService.reprice(id, form);

      if(error) {
        throw error;
      }

      const tradeItemsStore = useTradeItemsStore();

      tradeItemsStore.models.where('variation_order_id', parseInt(id as string)).delete();

      this.models.save(data);

      return data;
    },

    async submitVariationOrder(id: Id, form: ApiRequests['api.v1.variation-orders.submit']) {
      const [data, error] = await VariationOrderService.submit(id, form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async provisionVariationOrder(id: Id, signedParams: unknown) {
      const [data, error] = await VariationOrderService.provision(id, signedParams);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async declineSubmissionVariationOrder(
      id: Id,
      signedParams: unknown,
      form: ApiRequests['api.v1.variation-orders.decline-submission'],
    ) {
      const [data, error] = await VariationOrderService.declineSubmission(id, signedParams, form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async approveVariationOrder(id: Id, form: ApiRequests['api.v1.variation-orders.approve']) {
      const [data, error] = await VariationOrderService.approve(id, form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async completeVariationOrder(id: Id) {
      const [data, error] = await VariationOrderService.complete(id);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async transferVariationOrder(projectId: Id, id: Id, form: ApiRequests['api.v1.variation-orders.transfer']) {
      const [data, error] = await VariationOrderService.transfer(projectId, id, form);

      if(error) {
        throw error;
      }

      this.models.save(data.source_variation_order);

      if(data.credit_variation_order) {
        this.models.save(data.credit_variation_order);
      }

      return data;
    },

    async createVariationOrder(projectId: Id, request: ApiRequests['api.v1.variation-orders.store'] | FormData) {
      const [data, error] = await VariationOrderService.create(projectId, request);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async indexPackageVariationOrders(
      projectId: Id,
      packageId: Id,
      form: ApiRequests['api.v1.packages.variation-orders.index'] = {},
    ) {
      const [data, error] = await VariationOrderService.package.index(projectId, packageId, form);

      if(error) {
        throw error;
      }

      this.models.save(data.data);

      return data;
    },

    async storePackageVariationOrder(
      projectId: Id,
      packageId: Id,
      request: ApiRequests['api.v1.packages.variation-orders.store'] | FormData,
    ) {
      const [data, error] = await VariationOrderService.package.store(projectId, packageId, request);

      if(error) throw error;

      this.models.save(data);

      return data;
    },

    async backChargePackageVariationOrder(
      projectId: Id,
      packageId: Id,
      variationOrderId: Id,
      form: ApiRequests['api.v1.packages.variation-orders.back-charge'],
    ) {
      const [data, error] = await VariationOrderService.package.backCharge(
        projectId,
        packageId,
        variationOrderId,
        form,
      );

      if(error) {
        throw error;
      }

      this.models.save(data.source_variation_order);
      this.models.save(data.target_variation_order);

      return data;
    },

    async updateVariationOrder(id: Id, form: ApiRequests['api.v1.variation-orders.update']) {
      const [data, error] = await VariationOrderService.update(id, form);

      if(error) {
        throw error;
      }

      this.models.save(data.data);

      return data;
    },

    async updateNextActionVariationOrder(id: Id, form: ApiRequests['api.v1.variation-orders.next-action']) {
      const [data, error] = await VariationOrderService.nextAction(id, form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async updateForecastAmounts(id: Id, form: ApiRequests['api.v1.variation-orders.forecast-amounts']) {
      const [data, error] = await VariationOrderService.updateForecastAmounts(id, form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async updateBaselineForecast(id: Id, form: ApiRequests['api.v1.variation-orders.baseline-forecast']) {
      const [data, error] = await VariationOrderService.updateBaselineForecast(id, form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async sendNoticeForVariationOrder(id: Id, form: ApiRequests['api.v1.variation-orders.send-notice']) {
      const [data, error] = await VariationOrderService.sendNotice(id, form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async deleteVariationOrder(id: Id) {
      const [data, error] = await VariationOrderService.delete(id);

      if(error) {
        throw error;
      }

      this.models.destroy(data.id);

      return data;
    },

    async fetchVariationOrdersSummary(projectId: Id) {
      const [data, error] = await VariationOrderService.summary(projectId);

      if(error) {
        throw error;
      }

      return data as VariationOrdersSummary;
    },

    async requestVariationsSummaryReport(projectId: Id, form: ApiRequests['api.v1.variation-orders.summary-report']) {
      const [data, error] = await VariationOrderService.summaryReport(projectId, form);

      if(error) {
        throw error;
      }

      return data;
    },

    async comment(id: Id, form: ApiRequests['api.v1.variation-orders.comments']) {
      const commentsStore = useCommentsStore();
      const [data, error] = await VariationOrderService.comment(id, form);

      if(error) {
        throw error;
      }

      commentsStore.models.save(data);

      return data;
    },

    async media(id: Id) {
      const [data, error] = await VariationOrderService.media(id);

      if(error) {
        throw error;
      }

      return data;
    },

    async storeMedia(id: Id, form: ApiRequests['api.v1.claims.store-media'] | FormData) {
      const [data, error] = await VariationOrderService.storeMedia(id, form);

      if(error) {
        throw error;
      }

      return data;
    },

    async storeNotice(id: Id, form: ApiRequests['api.v1.variation-orders.notice.store'] | FormData) {
      const [data, error] = await VariationOrderService.notice.store(id, form);

      if(error) {
        throw error;
      }

      this.models.save(data.variation_order);

      return data;
    },

    async storePricingNotice(id: Id, form: ApiRequests['api.v1.variation-orders.pricing-notice.store'] | FormData) {
      const [data, error] = await VariationOrderService.pricingNotice.store(id, form);

      if(error) {
        throw error;
      }

      this.models.save(data.variation_order);

      return data;
    },

    async fetchCorrespondences(id: Id) {
      const correspondencesStores = useCorrespondencesStore();
      const [data, error] = await VariationOrderService.correspondence.index(id);

      if(error) {
        throw error;
      }

      correspondencesStores.models.save(data);

      return data;
    },

    async storeCorrespondence(id: Id, form: ApiRequests['api.v1.variation-orders.correspondence.store'] | FormData) {
      const correspondencesStores = useCorrespondencesStore();
      const [data, error] = await VariationOrderService.correspondence.store(id, form);

      if(error) {
        throw error;
      }

      correspondencesStores.models.save(data.correspondence);

      return data;
    },

    async fetchPackageApprovedVariations(projectId: Id, packageId: Id) {
      const [data, error] = await VariationOrderService.package.approvedIndex(projectId, packageId);

      if(error) throw error;

      this.models.save(data);

      return data;
    },

    async fetchSubcontractVariationOrders(projectId: Id) {
      const [data, error] = await VariationOrderService.subcontract.index(projectId);

      if(error) {
        throw error;
      }

      this.models.save(data.data);

      return data;
    },

    async fetchAdminIndex(query: ApiRequests['api.v1.admin.variation-orders.index'] & { page?: number } = {}) {
      const [data, error] = await VariationOrderService.admin.index(query);

      if(error) {
        throw error;
      }

      this.models.save(data.variation_orders.data);

      return data;
    },

    async fetchAdminVariationOrder(id: Id) {
      const [data, error] = await VariationOrderService.admin.show(id);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async updateAdminVariationOrder(id: Id, form: ApiRequests['api.v1.admin.variation-orders.update']) {
      const [data, error] = await VariationOrderService.admin.update(id, form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async deleteAdminVariationOrder(id: Id) {
      const [data, error] = await VariationOrderService.admin.delete(id);

      if(error) {
        throw error;
      }

      this.models.destroy(id);

      return data;
    },
  },
});
